import { ApiError } from "../api/http_client.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { GRAPHICS_QUALITY_ORDER, GraphicsProfile, GraphicsQuality, GraphicsSettings } from "./graphics_quality.js";
import { EMPTY_GRAPHICS_CONFIG, GraphicsRemoteConfig, normalizeGraphicsConfig } from "./graphics_remote_config.js";

const CACHE_KEY = "graphics_v1";
const CONFIG_TYPE = "graphics";

export class GraphicsConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CACHE_KEY ) !== null;
  }

  private static config: GraphicsRemoteConfig = GraphicsConfigService.readCached() ?? EMPTY_GRAPHICS_CONFIG;
  private static loaded = GraphicsConfigService.readCached() !== null;
  private static loading: Promise<GraphicsRemoteConfig> | null = null;

  static
  {
    if ( GraphicsConfigService.loaded )
      GraphicsSettings.setRemote( GraphicsConfigService.config );
  }

  private static readCached(): GraphicsRemoteConfig | null
  {
    const raw = PersistentCache.read<unknown>( CACHE_KEY );
    return raw ? normalizeGraphicsConfig( raw ) : null;
  }

  static get(): GraphicsRemoteConfig
  {
    return this.config;
  }

  static isLoaded(): boolean
  {
    return this.loaded;
  }

  static apply( config: GraphicsRemoteConfig ): void
  {
    this.config = config;
    this.loaded = true;
    GraphicsSettings.setRemote( config );
  }

  static async refresh(): Promise<GraphicsRemoteConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const server_config = await getContentConfig<unknown>( CONFIG_TYPE );
        this.apply( normalizeGraphicsConfig( server_config ) );
        PersistentCache.write( CACHE_KEY, this.config );
        console.log( "[GraphicsConfig] loaded", this.config );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 404 )
        {
          this.apply( EMPTY_GRAPHICS_CONFIG );
          PersistentCache.remove( CACHE_KEY );
          console.warn( "[GraphicsConfig] no server config, using bundled profiles" );
        }
        else
        {
          console.warn( "[GraphicsConfig] failed to fetch config", e );
        }
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }

  static describe(): { loaded: boolean; config: GraphicsRemoteConfig; effective: Record<GraphicsQuality, GraphicsProfile> }
  {
    const effective = {} as Record<GraphicsQuality, GraphicsProfile>;
    for ( const quality of GRAPHICS_QUALITY_ORDER )
      effective[quality] = { ...GraphicsSettings.baseProfile( quality ), ...( this.config.tiers[quality] ?? {} ) };
    return { loaded: this.loaded, config: this.config, effective };
  }
}
